import React from "react";
import { TypedToolInvocation } from "../utils";
import { Button } from "../../../../components/ui/button";
import { AlertTriangleIcon, CheckCircleIcon, FileTextIcon } from "lucide-react";
import { DocumentUpdateReviewDialog, DialogMode, EditState } from "./components/document-update-review-dialog";
import Spinner from "../../../../components/ui/spinner";
import {
    useApplyDocumentUpdateMutation,
    ApplyDocumentUpdateResult,
} from "../../../../data/mutations/apply-document-update";
import { cn } from "../../../../lib/utils";

type ProposalStatus = "pending" | "applied";

interface ApplyResult {
    appliedEditIds: string[];
    result: ApplyDocumentUpdateResult;
}

export function ProposeDocumentUpdateToolInvocation({
    invocation,
}: {
    invocation: TypedToolInvocation<"proposeDocumentUpdate">;
}) {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editStates, setEditStates] = React.useState<Record<string, EditState>>({});
    const [applyResult, setApplyResult] = React.useState<ApplyResult | null>(null);
    const [applyError, setApplyError] = React.useState<string | null>(null);
    const applyMutation = useApplyDocumentUpdateMutation();

    const result = invocation.result;
    const isLoading = invocation.state !== "result";

    const [prevProposalId, setPrevProposalId] = React.useState<string | null>(null);
    if (result && result.proposalId !== prevProposalId) {
        setPrevProposalId(result.proposalId);
        const initial: Record<string, EditState> = {};
        result.edits.forEach((e) => {
            initial[e.id] = "pending";
        });
        setEditStates(initial);
    }

    const handleEditStateChange = (id: string, state: EditState) => {
        setEditStates((prev) => ({ ...prev, [id]: state }));
    };

    const handleApply = () => {
        if (!result) return;
        setApplyError(null);

        const acceptedIds = Object.entries(editStates)
            .filter(([, state]) => state === "accepted")
            .map(([id]) => id);

        applyMutation
            .mutateAsync({
                proposalId: result.proposalId,
                editIds: acceptedIds,
            })
            .then((mutationResult) => {
                setApplyResult({
                    appliedEditIds: acceptedIds,
                    result: mutationResult,
                });
                setDialogOpen(false);
            })
            .catch(() => {
                setApplyError("Failed to apply changes. Please try again.");
            });
    };

    if (isLoading) {
        return (
            <div className="flex flex-row items-center gap-3 rounded-md border bg-accent p-2 px-4 text-sm font-medium">
                <FileTextIcon className="h-5 w-5 shrink-0" />
                <span>Analyzing document for updates...</span>
                <Spinner className="h-4 w-4" />
            </div>
        );
    }

    if (!result) return null;

    const totalEdits = result.edits.length;
    const hasBeenApplied = !!applyResult;
    const status: ProposalStatus = hasBeenApplied ? "applied" : "pending";

    const dialogMode: DialogMode = hasBeenApplied
        ? { mode: "applied", appliedEditIds: applyResult.appliedEditIds }
        : {
              mode: "review",
              editStates,
              onEditStateChange: handleEditStateChange,
              onApply: handleApply,
              isApplying: applyMutation.isPending,
              applyError,
          };

    return (
        <>
            <ProposalCard
                status={status}
                documentName={result.documentName}
                totalEdits={totalEdits}
                appliedCount={applyResult?.result.appliedCount}
                userChangesDetected={result.userChangesDetected}
                onReviewClick={() => setDialogOpen(true)}
            />
            <DocumentUpdateReviewDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                proposal={result}
                dialogMode={dialogMode}
            />
        </>
    );
}

interface ProposalCardProps {
    status: ProposalStatus;
    documentName: string;
    totalEdits: number;
    appliedCount?: number;
    userChangesDetected: boolean;
    onReviewClick: () => void;
}

function ProposalCard({
    status,
    documentName,
    totalEdits,
    appliedCount,
    userChangesDetected,
    onReviewClick,
}: ProposalCardProps) {
    const cardStyles = {
        pending: "border-primary bg-primary/20",
        applied: "border-confirmation bg-confirmation/20",
    };

    return (
        <div
            className={cn(
                "flex flex-row items-center gap-3 rounded-md border p-2 px-4 text-sm font-medium",
                cardStyles[status]
            )}
        >
            <FileTextIcon className="h-5 w-5 shrink-0" />
            <div className="flex flex-1 flex-col gap-0.5">
                <span>
                    {status === "pending" ? "Proposing" : "Proposed"}{" "}
                    <b>
                        {totalEdits} {totalEdits === 1 ? "update" : "updates"}
                    </b>{" "}
                    to "{documentName}"
                </span>
                <ProposalSubtext
                    status={status}
                    totalEdits={totalEdits}
                    appliedCount={appliedCount}
                    userChangesDetected={userChangesDetected}
                />
            </div>
            <ProposalAction status={status} onReviewClick={onReviewClick} />
        </div>
    );
}

interface ProposalSubtextProps {
    status: ProposalStatus;
    totalEdits: number;
    appliedCount?: number;
    userChangesDetected: boolean;
}

function ProposalSubtext({ status, totalEdits, appliedCount, userChangesDetected }: ProposalSubtextProps) {
    if (status === "applied") {
        return (
            <span className="text-xs text-muted-foreground">
                {appliedCount} {appliedCount === 1 ? "change" : "changes"} applied
            </span>
        );
    }

    return (
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
            {totalEdits} {totalEdits === 1 ? "change" : "changes"} proposed
            {userChangesDetected && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                    <AlertTriangleIcon className="h-3 w-3" />
                    User edits detected
                </span>
            )}
        </span>
    );
}

interface ProposalActionProps {
    status: ProposalStatus;
    onReviewClick: () => void;
}

function ProposalAction({ status, onReviewClick }: ProposalActionProps) {
    if (status === "applied") {
        return (
            <button
                onClick={onReviewClick}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 text-confirmation transition-colors hover:bg-confirmation/10"
            >
                <CheckCircleIcon className="h-4 w-4" />
                <span className="text-sm font-medium">Applied</span>
            </button>
        );
    }

    return (
        <Button onClick={onReviewClick} variant="outline" size="sm">
            Review Changes
        </Button>
    );
}
